/**
 * The franchise's own look, applied to the document.
 *
 * `GET /api/v1/central/branding` answers a name, a pair of colours, a favicon, an optional bag of raw
 * CSS variables and a **theme name**. Three of those interact in ways that are not obvious, and the
 * earlier attempt at this app shipped none of them — which is what the owner's 2026-07-31 inspection
 * rejected, along with the app wearing the manager's palette.
 *
 * **The brand colour wins over the theme.** `themes.css` sets a palette per `data-theme`, and
 * `primaryColor` is then written on top as an inline `--primary`. Legacy states the order plainly:
 * *"per-franchise primaryColor still overrides --primary on top of any theme, so the brand color
 * always wins over the theme's accent."*
 *
 * **The secondary colour deliberately does not.** It is exposed as `--brand-secondary` only, never as
 * `--secondary`, and legacy explains why: *"the shadcn theme uses --secondary as a LIGHT background
 * (Stone-100) — overriding it with a dark brand color (e.g. #1a1a1a) makes every bg-secondary surface
 * unreadable."* A franchise whose brand is near-black would otherwise turn half its own site into
 * black-on-black.
 *
 * **`classic` removes the attribute rather than setting it.** The default palette lives in `:root`, so
 * `data-theme="classic"` would select nothing and leave the defaults in place by accident. Removing it
 * is the same result stated on purpose.
 */

import { readableInk } from '@agos/shared'

export interface Branding {
  name: string
  displayName: string | null
  primaryColor: string | null
  secondaryColor: string | null
  faviconUrl: string | null
  themeName: string
  /** Raw `--*` overrides the franchise configured, applied last. */
  theme: Record<string, string>
}

export const DEFAULT_THEME = 'classic'
/** Legacy's fallback when a franchise has no colour — amber-500, the default palette's primary. */
export const DEFAULT_THEME_COLOR = '#f97316'

export function brandingOf(payload: unknown): Branding | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const text = (key: string): string | null => {
    const value = row[key]
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  }
  const name = text('name')
  const displayName = text('displayName')
  if (name === null && displayName === null) return null

  const raw = typeof row.theme === 'object' && row.theme !== null ? (row.theme as Record<string, unknown>) : {}
  const theme: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw)) {
    // Only custom properties. A stray `background: red` in this bag would be a franchise able to set
    // arbitrary inline styles on <html>.
    if (key.startsWith('--') && typeof value === 'string') theme[key] = value
  }

  return {
    name: name ?? displayName ?? '',
    displayName,
    primaryColor: text('primaryColor'),
    secondaryColor: text('secondaryColor'),
    faviconUrl: text('faviconUrl'),
    themeName: text('themeName') ?? DEFAULT_THEME,
    theme,
  }
}

/** `#f97316` → `25 95% 53%`. Three-digit hex expands; already-HSL components pass through. */
export function toHslComponents(color: string): string | null {
  const trimmed = color.trim()
  if (trimmed === '') return null
  if (/^\d+\s+\d+%\s+\d+%$/.test(trimmed)) return trimmed
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(trimmed)
  if (match === null) return null

  const digits = match[1]!
  const hex = digits.length === 3 ? digits.split('').map((digit) => digit + digit).join('') : digits
  const red = Number.parseInt(hex.slice(0, 2), 16) / 255
  const green = Number.parseInt(hex.slice(2, 4), 16) / 255
  const blue = Number.parseInt(hex.slice(4, 6), 16) / 255

  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2
  let hue = 0
  let saturation = 0
  if (max !== min) {
    const delta = max - min
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
    if (max === red) hue = (green - blue) / delta + (green < blue ? 6 : 0)
    else if (max === green) hue = (blue - red) / delta + 2
    else hue = (red - green) / delta + 4
    hue *= 60
  }
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`
}

/**
 * The short name iOS shows under the icon.
 *
 * Split on the dashes a restaurant name tends to carry — *Pizza Majstro – Šurany* becomes *Pizza
 * Majstro* — then clipped to twelve characters, which is roughly what fits.
 */
export function appleShortName(branding: Branding): string {
  const full = branding.displayName ?? branding.name
  const firstPart = full.split(/[-–—|·]/)[0] ?? full
  return firstPart.trim().slice(0, 12) || 'App'
}

export function themeColorOf(branding: Branding): string {
  return branding.primaryColor ?? DEFAULT_THEME_COLOR
}

/** Whether this theme name selects a sheet, or leaves `:root`'s defaults alone. */
export function selectsTheme(themeName: string): boolean {
  return themeName.trim() !== '' && themeName.trim() !== DEFAULT_THEME
}

export interface AppliedBranding {
  /** Custom properties to set on `<html>`, in order. */
  variables: Record<string, string>
  /** The `data-theme` value, or `null` to remove the attribute. */
  dataTheme: string | null
  documentTitle: string | null
  faviconUrl: string | null
  appleTitle: string
  themeColor: string
}

/**
 * What applying this branding comes to — computed here so it can be tested without a document.
 *
 * The order of `variables` matters: the franchise's raw `theme` bag is applied **last**, so a franchise
 * that sets `--primary` directly overrides even its own `primaryColor`. That is legacy's order and it
 * is the more specific instruction winning.
 */
export function applied(branding: Branding): AppliedBranding {
  const variables: Record<string, string> = {}

  if (branding.primaryColor !== null) {
    variables['--brand-primary'] = branding.primaryColor
    const hsl = toHslComponents(branding.primaryColor)
    if (hsl !== null) variables['--primary'] = hsl
    // §9.69 — the brand colour as *text*, which is not the same thing as the brand colour.
    //
    // A brand is chosen to fill a button, where the label sits on top of it and the contrast is the
    // label's problem. Used as ink on a card it has to carry the contrast itself, and most brands
    // cannot: the default amber is about 2:1 on white, well under the 4.5 a price has to make.
    //
    // `readableInk` walks the lightness along the same hue until it does — the rule the owner chose
    // for category colours (§9.49): *autoadjust closest hue for that color where text will be
    // readable*. Same rule, same helper, second surface.
    variables['--brand-ink'] = readableInk(branding.primaryColor, false)
  }
  // `--brand-secondary` only. See the note at the top of this file.
  if (branding.secondaryColor !== null) variables['--brand-secondary'] = branding.secondaryColor

  for (const [key, value] of Object.entries(branding.theme)) variables[key] = value

  return {
    variables,
    dataTheme: selectsTheme(branding.themeName) ? branding.themeName.trim() : null,
    documentTitle: branding.displayName,
    faviconUrl: branding.faviconUrl,
    appleTitle: appleShortName(branding),
    themeColor: themeColorOf(branding),
  }
}

// ─── §9.330: the scheme the manager has been writing and nothing read ────────

/**
 * Light or dark for the whole public site, from `GET /settings/design`.
 *
 * **The owner:** *"do we have dark theme already implemented in customer website? If yes, then when
 * I switched in in Design stránky - Schéma zákazníckej stránky - it did not worked when I switched
 * it between, I have always dark theme"*.
 *
 * §9.303 built the control and the `applyTheme` support for it, and **nothing on the customer site
 * ever read the value.** Measured: the manager persists `colorScheme` through `SERVER_FIELDS` on
 * `PUT /settings/design`, the endpoint is unauthenticated and the customer app already calls two of
 * its siblings - and `apps/customer` contained no reader and set no `data-scheme` anywhere. So the
 * switch could not do anything, whichever way it was thrown. The §9.303 e2e test asserted the
 * manager's two buttons and never that the customer site changed, which is why it stayed green.
 *
 * **`mode` is deliberately ignored, and this is the part to not get wrong.** The same payload carries
 * it - it is `auto` on the owner's data - and `applyTheme` in `@agos/shared` turns `auto` into dark
 * whenever the *visitor's* OS prefers dark. Applying that here would make the public site dark for
 * every customer with a dark phone, whatever the franchise chose, which is the opposite of the
 * setting's purpose. `mode` is the manager's own chrome; `colorScheme` is the public site's.
 *
 * Light is the default for an absent or unrecognised value: `themes.css` writes every theme against
 * `:root` and layers dark on top with `[data-scheme="dark"]`, so *no attribute* is the light palette
 * rather than an unstyled page.
 */
export type CustomerScheme = 'light' | 'dark'

export function schemeOf(payload: unknown): CustomerScheme {
  if (typeof payload !== 'object' || payload === null) return 'light'
  return (payload as { colorScheme?: unknown }).colorScheme === 'dark' ? 'dark' : 'light'
}
